import { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Box, Typography, TextField, Button, Link, Alert, CircularProgress } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useTranslation } from 'react-i18next';
import { apiClient } from '@services/api';
import { isAxiosError } from 'axios';
import { apiErrorMessage, isRequestCancelled, toDisplayError } from '../utils/apiError';

// Validation schema
const forgotPasswordSchema = z.object({
  email: z.string().min(1, 'auth.validation.emailRequired').email('auth.validation.emailInvalid'),
});

type ForgotPasswordFormData = z.infer<typeof forgotPasswordSchema>;

function ForgotPasswordPage() {
  const { t } = useTranslation();

  const [serverError, setServerError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState('');

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ForgotPasswordFormData>({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: {
      email: '',
    },
  });

  const onSubmit = async (data: ForgotPasswordFormData) => {
    setServerError(null);
    setIsSubmitting(true);

    try {
      await apiClient.post('/v1/auth/forgot-password', {
        email: data.email,
      });

      setSubmittedEmail(data.email);
      setIsSuccess(true);
    } catch (error: unknown) {
      // `error` is a string on some endpoints and { message, code } on others;
      // calling .includes() straight on it threw for the object form (REV-509).
      const apiMsg = apiErrorMessage(error);
      // Don't reveal if email exists - always show success for security
      // Only show error for technical issues
      if (isAxiosError(error) && !error.response && !isRequestCancelled(error)) {
        // REV-980: no response at all (timeout, dropped connection), so no
        // link was sent and nothing about the account was learned either.
        // Saying "check your email" here would be untrue.
        setServerError(toDisplayError(error, t('auth.errors.forgotFailed')));
      } else if (apiMsg?.includes('rate limit')) {
        setServerError(t('auth.errors.tooManyRequests'));
      } else {
        // Show success even on "user not found" for security
        setSubmittedEmail(data.email);
        setIsSuccess(true);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Success state
  if (isSuccess) {
    return (
      <Box sx={{ textAlign: 'center' }}>
        <CheckCircleIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
        <Typography variant="h5" component="h1" gutterBottom fontWeight={700}>
          {t('auth.forgotPassword.successTitle')}
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          {t('auth.forgotPassword.successMessage', { email: submittedEmail })}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
          {t('auth.forgotPassword.checkSpam')}
        </Typography>
        <Button component={RouterLink} to="/login" variant="outlined" startIcon={<ArrowBackIcon />}>
          {t('auth.forgotPassword.backToLogin')}
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom fontWeight={700}>
          {t('auth.forgotPassword.title')}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {t('auth.forgotPassword.subtitle')}
        </Typography>
      </Box>

      {/* Server error alert */}
      {serverError && (
        <Alert severity="error" sx={{ mb: 3 }} role="alert">
          {serverError}
        </Alert>
      )}

      {/* Forgot password form */}
      <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
        <TextField
          {...register('email')}
          label={t('auth.fields.email')}
          type="email"
          autoComplete="email"
          error={!!errors.email}
          helperText={errors.email ? t(errors.email.message as string) : undefined}
          disabled={isSubmitting}
          sx={{ mb: 3 }}
          slotProps={{ inputLabel: { shrink: true } }}
        />

        {/* Submit button */}
        <Button
          type="submit"
          variant="contained"
          size="large"
          fullWidth
          disabled={isSubmitting}
          sx={{ mb: 3 }}
        >
          {isSubmitting ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            t('auth.forgotPassword.submit')
          )}
        </Button>
      </Box>

      {/* Back to login link */}
      <Box sx={{ textAlign: 'center' }}>
        <Link
          component={RouterLink}
          to="/login"
          sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
        >
          <ArrowBackIcon fontSize="small" />
          {t('auth.forgotPassword.backToLogin')}
        </Link>
      </Box>
    </Box>
  );
}

export default ForgotPasswordPage;
